import React, { useState, useContext } from 'react';
import { useSocket } from '../../app/socketContext';
import { AuthContext } from '../lib';
import { SyncModalPortal } from '../lib/SyncToast';
import { getAppDisplayName } from '../../utils/appUtils';
import styles from './SyncIndicator.module.css';

export function SyncIndicator() {
  const { syncState } = useSocket();
  const authContext = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Only show if user has an active app connection
  const hasActiveApp = authContext.user?.app_connection_id;

  // Don't render if no active app connection
  if (!hasActiveApp) {
    return null;
  }

  // Get display name for the app
  const displayAppName = getAppDisplayName(syncState.appName);

  // Determine button state and text
  const isActive = syncState.isActive;
  const hasHistory = syncState.history && syncState.history.length > 0;
  const isDisabled = !isActive && !hasHistory;
  
  let buttonText = 'No Sync Data';
  let tooltipText = 'No sync history available';
  
  // Helper function to format time
  const formatSyncTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just Now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isActive) {
    // Currently syncing
    if (syncState.status === 'syncing') {
      buttonText = 'Syncing...';
      tooltipText = `${displayAppName} - ${syncState.currentMessage}`;
    } else if (syncState.status === 'complete') {
      buttonText = 'Sync Complete';
      tooltipText = `${displayAppName} - Sync completed successfully`;
    } else if (syncState.status === 'error') {
      buttonText = 'Sync Error';
      tooltipText = `${displayAppName} - Sync error: ${syncState.currentMessage}`;
    }
  } else if (hasHistory) {
    // Not active but has previous sync history
    const lastSync = syncState.history[syncState.history.length - 1];
    const timeAgo = formatSyncTime(lastSync.timestamp);
    buttonText = `Last sync: ${timeAgo}`;
    tooltipText = `View last sync details (${new Date(lastSync.timestamp).toLocaleString()})`;
  } else {
    // No sync history available
    buttonText = '';
    tooltipText = 'No sync has been performed yet';
  }

  return (
    <>
      {/* Sync indicator button */}
      <div className={styles.buttonContainer}>
        <button
          className={`${styles.syncIndicator} ${isActive ? styles[syncState.status] : isDisabled ? styles.disabled : styles.inactive}`}
          onClick={isDisabled ? undefined : () => setShowModal(true)}
          disabled={isDisabled}
          title={!isDisabled ? tooltipText : undefined}
          onMouseEnter={() => isDisabled && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className={styles.blinkingDot}></div>
          <span className={styles.syncText}>
            {buttonText}
          </span>
        </button>
        
        {/* Custom tooltip for disabled state */}
        {isDisabled && showTooltip && (
          <div className={styles.tooltip}>
            {tooltipText}
          </div>
        )}
      </div>

      {/* Modal for detailed sync progress */}
      {showModal && !isDisabled && (
        <SyncModalPortal 
          onClose={() => setShowModal(false)}
          syncStatus={syncState.status}
          currentMessage={syncState.currentMessage}
          appName={syncState.appName}
          progress={syncState.progress}
          syncHistory={syncState.history}
          currentStage={syncState.currentStage}
        />
      )}
    </>
  );
}